import {
  Directive,
  DoCheck,
  ElementRef,
  FactoryProvider,
  Inject,
  Injector,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { UpgradeComponent } from '@angular/upgrade/static';
import { IInjectorService } from '@angular/upgrade/static/src/common/angular1';
import { camelCase } from 'lodash';

/**
 * A set of helpers to upgrade Ng1 services and components
 */
/* istanbul ignore next */
export class UpgradeNg1Helpers {
  /**
   * Use for NG1 services and providers to upgrade (CM)
   */
  public static upgradeProvider(provider: string): FactoryProvider {
    return {
      provide: provider,
      useFactory: UpgradeNg1Helpers.getInjector(provider),
      deps: ['$injector'],
    };
  }

  /**
   * Use for NG1 components to upgrade
   * `selector` should be the element string e.g. `side-nav`
   * `inputs` - optional, should be an array of strings with that match the names of the
   * `<` and `&` bindings in the AngularJS component that is being wrapped (CM)
   * `output` - optional array of strings of event emitters.
   */
  public static upgradeComponent(opts: { selector: string; inputs?: string[]; outputs?: string[] }) {
    const { selector, inputs, outputs } = opts;

    // This Angular directive will act as an interface to the "upgraded" AngularJS component
    // The names of the input and output properties here must match the names of the
    // `<` and `&` bindings in the AngularJS component that is being wrapped
    @Directive({
      selector,
      inputs,
      outputs,
    })
    // tslint:disable:max-classes-per-file
    class Ng1ComponentWrapper extends UpgradeComponent implements OnInit, OnChanges, DoCheck, OnDestroy {
      constructor(@Inject(ElementRef) elementRef: ElementRef, @Inject(Injector) injector: Injector) {
        // We must pass the name of the directive as used by AngularJS to the super
        super(camelCase(selector), elementRef, injector);
      }

      // For this class to work when compiled with AoT, we must implement these lifecycle hooks
      // because the AoT compiler will not realise that the super class implements them
      ngOnInit() {
        super.ngOnInit();
      }

      ngOnChanges(changes: SimpleChanges) {
        super.ngOnChanges(changes);
      }

      ngDoCheck() {
        super.ngDoCheck();
      }

      ngOnDestroy() {
        super.ngOnDestroy();
      }
    }

    return Ng1ComponentWrapper;
  }

  private static getInjector(provider: string): FactoryProvider['useFactory'] {
    return function(injector: IInjectorService) {
      return injector.get(provider);
    };
  }
}
